// src/pages/AdminRoles.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  usePermissions,
  cloneLimitedConfig,
  cloneMatrix,
  listPages,
} from "../utils/permissions";
import { getCurrentUser } from "../utils/auth";
import {
  DEFAULT_LIMITED_CONFIG,
  PERMISSION_LEVELS,
} from "../config/permissions";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const UNSAVED_MESSAGE = "You have unsaved changes. Leave without saving?";

const LEVEL_LABELS = {
  None: "No Access",
  Read: "Read Only",
  Limited: "Restricted",
  Full: "Full Access",
};

const PERMISSION_LEVEL_STYLES = {
  None: {
    shortLabel: "None",
    badgeClass: "bg-red-50 text-red-700 border-red-200",
    selectClass: "border-red-300 text-red-600 focus:ring-red-300 focus:border-red-400",
  },
  Read: {
    shortLabel: "View",
    badgeClass: "bg-blue-50 text-blue-800 border-blue-200",
    selectClass: "border-blue-300 text-blue-700 focus:ring-blue-300 focus:border-blue-400",
  },
  Limited: {
    shortLabel: "Limited",
    badgeClass: "bg-purple-50 text-purple-800 border-purple-200",
    selectClass: "border-purple-300 text-purple-700 focus:ring-purple-300 focus:border-purple-400",
  },
  Full: {
    shortLabel: "Full",
    badgeClass: "bg-emerald-50 text-emerald-900 border-emerald-200",
    selectClass: "border-emerald-300 text-emerald-700 focus:ring-emerald-300 focus:border-emerald-400",
  },
};

function getPermissionLevelMeta(level) {
  return PERMISSION_LEVEL_STYLES[level] || PERMISSION_LEVEL_STYLES.None;
}

const LIMITED_FEATURES = [
  { key: "addRow", label: "Add Row" },
  { key: "importData", label: "Import Data" },
  { key: "exportData", label: "Export Data" },
  { key: "deleteRows", label: "Delete Rows" },
  { key: "saveChanges", label: "Save Changes" },
  { key: "getIbCode", label: "Get IB Code" },
];

const FIELD_ACCESS_OPTIONS = [
  { value: "read", label: "View" },
  { value: "edit", label: "Edit" },
  { value: "auto", label: "Auto" },
];

const MYCON_FIELDS = [
  { key: "order_no", label: "Order No" },
  { key: "status", label: "Status" },
  { key: "m_closed", label: "M-Closed" },
  { key: "drayage", label: "Drayage" },
  { key: "warehouse", label: "Warehouse" },
  { key: "mbl_no", label: "MBL No" },
  { key: "container", label: "Container No" },
  { key: "etd", label: "ETD" },
  { key: "eta", label: "ETA" },
  { key: "pod", label: "POD" },
  { key: "arrived", label: "Arrived" },
  { key: "lfd", label: "LFD" },
  { key: "appt_date", label: "Appt Date" },
  { key: "lrd", label: "LRD" },
  { key: "delivered_dt", label: "Delivered" },
  { key: "emptied_dt", label: "Emptied" },
  { key: "returned_date", label: "Returned" },
];

const SCOPE_OPTIONS = [
  { value: { type: "all", field: null }, label: "All data" },
  { value: { type: "company", field: "drayage" }, label: "Only matching Drayage" },
  { value: { type: "company", field: "warehouse" }, label: "Only matching Warehouse" },
];
function serializeScope(scope) {
  if (!scope || scope.type === "all") return "all";
  return `${scope.type}:${scope.field || ""}`;
}

function parseScope(value) {
  if (value === "all") return { type: "all", field: null };
  const [type, field] = value.split(":");
  return { type: type || "all", field: field || null };
}

function composeErrorMessage(raw) {
  const text = (raw || "").trim();
  if (!text) return "An unexpected error occurred. Please try again.";
  if (/forbidden/i.test(text) || /403/.test(text)) {
    return "";
  }
  return text;
}


export default function AdminRoles() {
  const { matrix, limited, loading, error, save, roles: knownRoles } = usePermissions();
  const pages = useMemo(() => listPages(), []);
  const [workingMatrix, setWorkingMatrix] = useState(() => cloneMatrix(matrix));
  const [workingLimited, setWorkingLimited] = useState(() => cloneLimitedConfig(limited));
  const [baseline, setBaseline] = useState(() => ({
    matrix: cloneMatrix(matrix),
    limited: cloneLimitedConfig(limited),
  }));
  const [roleDefaultPages, setRoleDefaultPages] = useState({});
  const [roleDefaultsLoading, setRoleDefaultsLoading] = useState(false);
  const [roleDefaultsError, setRoleDefaultsError] = useState("");
  const [roleDefaultSaving, setRoleDefaultSaving] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [detail, setDetail] = useState({ open: false, role: "", key: "" });
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const pendingNavigationRef = useRef(null);
  const suppressPopRef = useRef(false);

  const dirty = useMemo(() => {
    return (
      !deepEqual(workingMatrix, baseline.matrix) || !deepEqual(workingLimited, baseline.limited)
    );
  }, [workingMatrix, workingLimited, baseline]);

  const requestNavigation = useCallback((callback) => {
    pendingNavigationRef.current = callback;
    setShowLeaveConfirm(true);
  }, []);

  const confirmNavigation = useCallback(() => {
    const pending = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setShowLeaveConfirm(false);
    if (pending) pending();
  }, []);

  const cancelNavigation = useCallback(() => {
    pendingNavigationRef.current = null;
    setShowLeaveConfirm(false);
  }, []);

  useEffect(() => {
    const nextMatrix = cloneMatrix(matrix);
    const nextLimited = cloneLimitedConfig(limited);
    setWorkingMatrix(nextMatrix);
    setWorkingLimited(nextLimited);
    setBaseline({ matrix: nextMatrix, limited: nextLimited });
    pendingNavigationRef.current = null;
    setShowLeaveConfirm(false);
  }, [matrix, limited]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = UNSAVED_MESSAGE;
      return UNSAVED_MESSAGE;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const history = window.history;
    const nativePush = history.pushState.bind(history);
    const nativeReplace = history.replaceState.bind(history);

    const wrapMethod = (method) => {
      return (...args) => {
        requestNavigation(() => method(...args));
      };
    };

    history.pushState = wrapMethod(nativePush);
    history.replaceState = wrapMethod(nativeReplace);

    const handlePopState = () => {
      if (suppressPopRef.current) {
        suppressPopRef.current = false;
        return;
      }
      history.go(1);
      requestNavigation(() => {
        suppressPopRef.current = true;
        history.go(-1);
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      history.pushState = nativePush;
      history.replaceState = nativeReplace;
      window.removeEventListener("popstate", handlePopState);
    };
  }, [dirty, requestNavigation]);

  const roleList = useMemo(() => {
    const ordered = [];
    const seen = new Set();

    const push = (value) => {
      if (value === undefined || value === null) return;
      const name = String(value).trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      ordered.push(name);
    };

    (knownRoles || []).forEach(push);
    Object.keys(workingMatrix || {}).forEach(push);
    return ordered;
  }, [knownRoles, workingMatrix]);
  const rolesKey = useMemo(
    () => (roleList || []).map((role) => String(role || "").toLowerCase()).join("|"),
    [roleList]
  );

  const [columnOrder, setColumnOrder] = useState(roleList);

  useEffect(() => {
    setColumnOrder((prev) => {
      if (!prev || !prev.length) {
        return roleList;
      }
      const preserved = prev.filter((role) => roleList.includes(role));
      const additions = roleList.filter((role) => !preserved.includes(role));
      return [...preserved, ...additions];
    });
  }, [roleList]);

  const orderedRoles = columnOrder && columnOrder.length ? columnOrder : roleList;

  useEffect(() => {
    let cancelled = false;
    async function loadRoleDefaults() {
      setRoleDefaultsLoading(true);
      setRoleDefaultsError("");
      try {
        const user = getCurrentUser();
        if (!user?.email) {
          throw new Error("missing_user");
        }
        const headers = {
          "X-User-Email": user.email,
          "X-User-Role": user.role || "",
        };
        const resp = await fetch(`${API_BASE}/api/admin/roles/default-pages`, { headers });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data?.ok === false) {
          throw new Error(data?.error || "load_failed");
        }
        if (!cancelled) {
          setRoleDefaultPages(data.roles || {});
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("admin roles: failed to load default pages", err);
          setRoleDefaultPages({});
          setRoleDefaultsError("Unable to load role defaults; selections start blank.");
        }
      } finally {
        if (!cancelled) {
          setRoleDefaultsLoading(false);
        }
      }
    }
    loadRoleDefaults();
    return () => {
      cancelled = true;
    };
  }, [rolesKey]);

  const moveRoleToSecond = useCallback((role) => {
    setColumnOrder((prev) => {
      const source = prev && prev.length ? prev : orderedRoles;
      if (!source.includes(role)) {
        return source;
      }
      const next = source.filter((item) => item !== role);
      next.splice(1, 0, role);
      return next;
    });
  }, [orderedRoles]);

  const findRoleKey = useCallback((source, role) => {
    if (!source || !role) return role;
    const lower = String(role).toLowerCase();
    const key = Object.keys(source).find((name) => String(name).toLowerCase() === lower);
    return key || role;
  }, []);

  const getRoleDefaultValue = useCallback(
    (role) => {
      if (!role) return "";
      const key = findRoleKey(roleDefaultPages, role);
      if (!key) return "";
      return roleDefaultPages?.[key] || "";
    },
    [roleDefaultPages, findRoleKey]
  );

  const getAllowedDefaultOptions = useCallback(
    (role) => {
      const roleKey = findRoleKey(workingMatrix, role);
      const mapping = workingMatrix?.[roleKey] || {};
      return pages
        .filter((page) => {
          const level = mapping[page.id] || "None";
          return level && String(level).toLowerCase() !== "none";
        })
        .map((page) => ({ id: page.id, label: page.label }));
    },
    [findRoleKey, workingMatrix, pages]
  );

  const handleRoleDefaultChange = useCallback(
    async (role, pageId) => {
      if (!role) return;
      const canonicalRole = findRoleKey(workingMatrix, role) || role;
      setRoleDefaultSaving((prev) => ({ ...prev, [canonicalRole]: true }));
      try {
        const user = getCurrentUser();
        if (!user?.email) {
          throw new Error("missing_user");
        }
        const headers = {
          "Content-Type": "application/json",
          "X-User-Email": user.email,
          "X-User-Role": user.role || "",
        };
        const resp = await fetch(
          `${API_BASE}/api/admin/roles/${encodeURIComponent(canonicalRole)}/default-page`,
          {
            method: "PATCH",
            headers,
            body: JSON.stringify({ default_page_id: pageId || null }),
          }
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data?.ok === false) {
          const message = data?.error || data?.message || "Failed to update default page.";
          window.alert(message);
        } else {
          setRoleDefaultPages((prev) => ({ ...prev, [canonicalRole]: data?.role_default_page_id || "" }));
          setMessage(`Default page updated for ${canonicalRole}.`);
        }
      } catch (err) {
        console.error(err);
        window.alert("Failed to update default page.");
      } finally {
        setRoleDefaultSaving((prev) => {
          const next = { ...prev };
          delete next[canonicalRole];
          return next;
        });
      }
    },
    [findRoleKey, workingMatrix, setMessage]
  );

  const ensureLimited = useCallback((role, key) => {
    setWorkingLimited((prev) => {
      const next = cloneLimitedConfig(prev);
      if (!next[role]) next[role] = {};
      if (!next[role][key]) {
        const defaults = DEFAULT_LIMITED_CONFIG[role]?.[key];
        next[role][key] = defaults
          ? cloneLimitedConfig({ tmp: defaults }).tmp
          : {
              scope: { type: "all", field: null },
              features: {
                addRow: false,
                importData: false,
                exportData: false,
                deleteRows: false,
                saveChanges: false,
              },
              fieldAccess: {},
            };
      }
      return next;
    });
  }, []);

  const onLevelChange = (role, pageId, level) => {
    const actualRole = findRoleKey(workingMatrix, role);
    setWorkingMatrix((prev) => {
      const next = cloneMatrix(prev);
      const key = findRoleKey(next, role);
      if (!next[key]) next[key] = {};
      next[key][pageId] = level;
      return next;
    });
    if (level === "Limited") {
      const page = pages.find((p) => p.id === pageId);
      if (page?.limitedConfigKey) ensureLimited(actualRole, page.limitedConfigKey);
    }
  };

  const openDetail = (role, key) => {
    const actualRole = findRoleKey(workingLimited, role);
    ensureLimited(actualRole, key);
    setDetail({ open: true, role: actualRole, key });
  };

  const closeDetail = () => setDetail({ open: false, role: "", key: "" });

  const updateLimitedConfig = (role, key, updater) => {
    setWorkingLimited((prev) => {
      const next = cloneLimitedConfig(prev);
      const targetRole = findRoleKey(next, role);
      if (!next[targetRole]) next[targetRole] = {};
      const current = next[targetRole][key] || {};
      next[targetRole][key] = updater(current);
      return next;
    });
  };

  const onAddRole = useCallback(() => {
    const input = window.prompt("Enter new role name");
    if (input === null) return;
    const name = input.trim();
    if (!name) {
      setMessage("Role name cannot be empty.");
      return;
    }
    const exists = roleList.some((r) => r.toLowerCase() === name.toLowerCase());
    if (exists) {
      setMessage(`Role "${name}" already exists.`);
      return;
    }
    setWorkingMatrix((prev) => {
      const next = cloneMatrix(prev);
      if (!next[name]) next[name] = {};
      pages.forEach((page) => {
        next[name][page.id] = "None";
      });
      return next;
    });
    setWorkingLimited((prev) => {
      const next = cloneLimitedConfig(prev);
      if (!next[name]) next[name] = {};
      return next;
    });
    setMessage(`Role "${name}" added. Adjust permissions and save to apply.`);
  }, [roleList, pages]);

  const onDeleteRole = useCallback((role) => {
    if (!role) return;
    if (role.toLowerCase() === "admin") {
      setMessage("Admin role cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete the "${role}" role? Changes apply after Save.`)) {
      return;
    }
    setWorkingMatrix((prev) => {
      const next = cloneMatrix(prev);
      const key = findRoleKey(next, role);
      if (key && next[key]) {
        delete next[key];
      }
      return next;
    });
    setWorkingLimited((prev) => {
      const next = cloneLimitedConfig(prev);
      const key = findRoleKey(next, role);
      if (key && next[key]) {
        delete next[key];
      }
      return next;
    });
    if (detail.open && detail.role && detail.role.toLowerCase() === role.toLowerCase()) {
      setDetail({ open: false, role: "", key: "" });
    }
    setMessage(`Role "${role}" marked for deletion. Save to apply.`);
  }, [detail, findRoleKey]);

  const onSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const currentMatrix = cloneMatrix(workingMatrix);
      const currentLimited = cloneLimitedConfig(workingLimited);
      await save(currentMatrix, currentLimited);
      setBaseline({ matrix: currentMatrix, limited: currentLimited });
      setMessage("Permissions saved successfully.");
    } catch (err) {
      console.error(err);
      setMessage(composeErrorMessage(err.message));
    } finally {
      setSaving(false);
    }
  };
  const onReset = () => {
    setWorkingMatrix(cloneMatrix(baseline.matrix));
    setWorkingLimited(cloneLimitedConfig(baseline.limited));
    setMessage("Changes reverted to the last saved state.");
  };

  const renderCell = (role, page) => {
    const roleKey = findRoleKey(workingMatrix, role);
    const level = workingMatrix?.[roleKey]?.[page.id] || "None";
    const showDetailButton = level === "Limited" && page.limitedConfigKey;
    const meta = getPermissionLevelMeta(level);
    return (
      <td key={`${page.id}-${role}`} className="border px-3 py-2 align-top">
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={`border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${meta.selectClass}`}
            value={level}
            aria-label={`Set ${role} access for ${page.label}`}
            onChange={(e) => onLevelChange(role, page.id, e.target.value)}
          >
            {PERMISSION_LEVELS.map((lv) => (
              <option key={lv} value={lv}>
                {LEVEL_LABELS[lv] || lv}
              </option>
            ))}
          </select>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none ${meta.badgeClass}`}
          >
            {meta.shortLabel}
          </span>
          {showDetailButton && (
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => openDetail(role, page.limitedConfigKey)}
            >
              Configure
            </button>
          )}
        </div>
      </td>
    );
  };

  const detailConfig = detail.open
    ? workingLimited?.[detail.role]?.[detail.key] || DEFAULT_LIMITED_CONFIG[detail.role]?.[detail.key]
    : null;

  const loadError = !message && error ? composeErrorMessage(error) : "";

  return (
    <div className="p-4 space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Role Permission Matrix</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage each role's access across the primary modules. Choose "Restricted" to fine-tune limited permissions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-3 py-1 rounded border text-sm"
            onClick={onAddRole}
            disabled={loading || saving}
          >
            Add Role
          </button>
          <button
            type="button"
            className="px-3 py-1 rounded border text-sm"
            onClick={onReset}
            disabled={loading || saving}
          >
            Reset
          </button>
          <button
            type="button"
            className="px-3 py-1 rounded bg-blue-600 text-white text-sm disabled:opacity-60"
            onClick={onSave}
            disabled={!dirty || loading || saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </header>

      {message && (
        <div className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
          {message}
        </div>
      )}
      {loadError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {loadError}
        </div>
      )}
      {roleDefaultsError && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {roleDefaultsError}
        </div>
      )}

      <div className="overflow-auto border rounded">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="border px-3 py-2 text-left text-sm font-semibold">Page</th>
              {orderedRoles.map((role, idx) => {
                const adminLocked = role.toLowerCase() === "admin";
                return (
                  <th key={role} className="border px-3 py-2 text-sm font-semibold">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        {idx >= 1 && (
                          <button
                            type="button"
                            className="text-gray-500 hover:text-gray-700 text-xs"
                            title="Move column to second position"
                            onClick={() => moveRoleToSecond(role)}
                          >
                            ←
                          </button>
                        )}
                        <span>{role}</span>
                      </div>
                      {!adminLocked && (
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:underline"
                          onClick={() => onDeleteRole(role)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-gray-50/50">
              <td className="border px-3 py-2 text-sm font-medium">Default page</td>
              {orderedRoles.map((role) => {
                const currentValue = getRoleDefaultValue(role) || "";
                const options = getAllowedDefaultOptions(role);
                const savingKey = findRoleKey(roleDefaultSaving, role);
                const isBusy = !!(savingKey && roleDefaultSaving[savingKey]);
                return (
                  <td key={`default-${role}`} className="border px-3 py-2 align-top">
                    {options.length > 0 ? (
                      <div className="space-y-1">
                        <select
                          className="w-full border rounded px-2 py-1 text-sm"
                          value={currentValue}
                          onChange={(e) => handleRoleDefaultChange(role, e.target.value || null)}
                          disabled={roleDefaultsLoading || isBusy}
                        >
                          <option value="">Auto (first allowed)</option>
                          {options.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {isBusy && <div className="text-xs text-blue-600">Saving...</div>}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">No accessible pages.</div>
                    )}
                  </td>
                );
              })}
            </tr>
            {pages.map((page) => (
              <tr key={page.id} className="even:bg-gray-50/40">
                <td className="border px-3 py-2 text-sm font-medium">
                  <div>{page.label}</div>
                  <div className="text-xs text-gray-500">{page.route}</div>
                </td>
                {orderedRoles.map((role) => renderCell(role, page))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <footer className="text-xs text-gray-500">
        {dirty ? "You have unsaved changes." : loading ? "Loading permissions..." : "All changes are saved."}
      </footer>
      {detail.open && detailConfig && (
        <LimitedDetailPanel
          role={detail.role}
          configKey={detail.key}
          config={detailConfig}
          onClose={closeDetail}
          onChange={(next) => updateLimitedConfig(detail.role, detail.key, () => next)}
        />
      )}
      {showLeaveConfirm && (
        <LeaveConfirmDialog message={UNSAVED_MESSAGE} onCancel={cancelNavigation} onLeave={confirmNavigation} />
      )}
    </div>
  );
}

function LimitedDetailPanel({ role, configKey, config, onClose, onChange }) {
  const [localConfig, setLocalConfig] = useState(() => cloneLimitedConfig({ tmp: config }).tmp);

  useEffect(() => {
    setLocalConfig(cloneLimitedConfig({ tmp: config }).tmp);
  }, [config]);

  const commit = (next) => {
    setLocalConfig(next);
    onChange(next);
  };

  const onScopeChange = (event) => {
    const value = event.target.value;
    const scope = parseScope(value);
    commit({
      ...localConfig,
      scope,
    });
  };

  const onFeatureToggle = (feature) => {
    commit({
      ...localConfig,
      features: {
        ...localConfig.features,
        [feature]: !localConfig.features?.[feature],
      },
    });
  };

  const onFieldAccessChange = (field, value) => {
    commit({
      ...localConfig,
      fieldAccess: {
        ...localConfig.fieldAccess,
        [field]: value,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-[640px] max-h-[80vh] overflow-auto rounded-lg bg-white shadow-lg p-5 space-y-4">
        <header className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{`${role} - Limited permissions`}</h2>
            <p className="text-xs text-gray-600">Adjust My Containers fields and features for this role.</p>
          </div>
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            Close
          </button>
        </header>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Data scope</h3>
          <select
            className="w-full border rounded px-2 py-1 text-sm"
            value={serializeScope(localConfig.scope)}
            onChange={onScopeChange}
          >
            {SCOPE_OPTIONS.map((option) => (
              <option key={serializeScope(option.value)} value={serializeScope(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Available features</h3>
          <div className="grid grid-cols-2 gap-2">
            {LIMITED_FEATURES.map((feature) => (
              <label key={feature.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!localConfig.features?.[feature.key]}
                  onChange={() => onFeatureToggle(feature.key)}
                />
                {feature.label}
              </label>
            ))}
          </div>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Field permissions</h3>
          <div className="border rounded max-h-64 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Field</th>
                  <th className="px-3 py-2 text-left">Permission</th>
                </tr>
              </thead>
              <tbody>
                {MYCON_FIELDS.map((field) => (
                  <tr key={field.key} className="border-t">
                    <td className="px-3 py-2">{field.label}</td>
                    <td className="px-3 py-2">
                      <select
                        className="border rounded px-2 py-1 text-sm"
                        value={localConfig.fieldAccess?.[field.key] || "read"}
                        onChange={(e) => onFieldAccessChange(field.key, e.target.value)}
                      >
                        {FIELD_ACCESS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            "Auto" means the system enforces the value and users cannot edit it.
          </p>
        </section>

        <footer className="flex justify-end">
          <button
            type="button"
            className="px-3 py-1 rounded bg-blue-600 text-white text-sm"
            onClick={onClose}
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}

function LeaveConfirmDialog({ message, onCancel, onLeave }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-lg bg-white shadow-lg p-5 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Unsaved changes</h2>
          <p className="text-sm text-gray-600">{message}</p>
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-3 py-1 rounded border border-gray-300 text-sm"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-3 py-1 rounded bg-red-600 text-white text-sm"
            onClick={onLeave}
          >
            Leave
          </button>
        </div>
      </div>
    </div>
  );
}

function deepEqual(a, b) {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) {
        return false;
      }
      if (!deepEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  }
  return a === b;
}
